import React, { type ReactNode } from 'react';
import { styled } from '@compiled/react';
import Button from '@atlaskit/button';
import ArrowLeftIcon from '@atlaskit/icon/glyph/arrow-left';
import { Box, xcss } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';
import { SeenIt } from '@atlassian/jira-business-onboarding-common';
import { useIntl } from '@atlassian/jira-intl';
import {
	FireScreenAnalytics,
	ContextualAnalyticsData,
	SCREEN,
} from '@atlassian/jira-product-analytics-bridge';
import { useTenantContext } from '@atlassian/jira-tenant-context-controller';
import { PROJECT_CREATE_IMPORTER_SEEN_IT_FLAG } from '../../common/constants';
import type { ImportType } from '../../common/constants/import-type';
import { X2C_SCREENS } from '../../common/constants/metrics';
import { PageDescription, PageHeading } from '../../common/ui';
import { SwitchBackToOldExperienceLink } from '../../common/ui/old-experience-link';
import { hasOnlyJWMLicense } from '../../common/utils';
import useAvailableProducts from '../../common/utils/available-products';
import { useProjectSpecificContent } from '../../common/utils/project-specific-content';
import { useProjectData } from '../../controllers/import-state';
import { useSource } from '../../controllers/import-state/selectors/source';
import { ImportFromOtherTools } from './import-from-other-tools';
import messages from './messages';
import { UploadCSVDropZone } from './upload-csv-dropzone';

type UploadScreenProps = {
	onReturn: () => void;
	onNext: (importType?: ImportType) => void;
};

export const UploadScreen = ({ onReturn, onNext }: UploadScreenProps) => {
	const { formatMessage } = useIntl();
	const { isSiteAdmin } = useTenantContext();
	const { availableProducts } = useAvailableProducts();
	const showReturnButton = isSiteAdmin && !hasOnlyJWMLicense(availableProducts);
	const [{ type }] = useProjectData();
	const [source] = useSource();
	const { uploadHeading, uploadDescription } = useProjectSpecificContent(type, source);

	// TODO: Don't show general importer link when user is JIRA_ADMIN but not SITE_ADMIN
	return (
		<ContextualAnalyticsData sourceName={X2C_SCREENS.UPLOAD_CSV} sourceType={SCREEN}>
			<Wrapper data-testid="project-create-importer-upload-flow.ui.upload-screen.wrapper">
				{showReturnButton && (
					<Button
						testId="project-create-importer-upload-flow.ui.upload-screen.return-button"
						onClick={onReturn}
						appearance="subtle"
						iconBefore={<ArrowLeftIcon label="Arrow Left" />}
						aria-label={formatMessage(messages.return)}
					>
						{formatMessage(messages.return)}
					</Button>
				)}
				<Box xcss={headingWrapperStyles}>
					<PageHeading>{formatMessage(uploadHeading)}</PageHeading>
					<PageDescription>
						{formatMessage(uploadDescription, {
							link: (content: ReactNode) => <SwitchBackToOldExperienceLink content={content} />,
						})}
					</PageDescription>
				</Box>
				<UploadCSVDropZone onNext={onNext} />
				<ImportFromOtherTools onNext={onNext} />
				<SeenIt flag={PROJECT_CREATE_IMPORTER_SEEN_IT_FLAG} />
				<FireScreenAnalytics />
			</Wrapper>
		</ContextualAnalyticsData>
	);
};

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const Wrapper = styled.div({
	maxWidth: '1200px',
	padding: `${token('space.300', '24px')} ${token('space.1000', '80px')}`,
});

const headingWrapperStyles = xcss({
	marginTop: 'space.600',
	marginBottom: 'space.100',
});
